import React, { useContext, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import FormDataContext from 'integrations/contexts/FormDataContext';
import useFetch from 'integrations/hooks/useFetch';

import { StatusIcon } from 'components/common';
import urlWithEventHubParams from 'enterprise-integrations/helpers/urlWithEventHubParams';

import { ApiRoutes } from './common/Routes';
import { toAzureLogsInputCreateRequest } from './common/formDataAdapter';
import type { ErrorMessageType, HandleSubmitType } from './types';

import FormWrap from '../common/FormWrap';

const Container = styled.div(
  ({ theme }) => css`
    border: 1px solid ${theme.colors.variant.darkest.default};
    margin: 25px 0;
    padding: 15px;
    border-radius: 4px;
    width: max-content;
  `,
);

const Subheader = styled.h3`
  margin: 0 0 10px;
`;

const ReviewItems = styled.ul(
  ({ theme }) => css`
    list-style: none;
    margin: 0 0 25px 10px;
    padding: 0;

    li {
      padding: 5px;

      &:nth-of-type(odd) {
        background-color: ${theme.colors.table.row.backgroundStriped};
      }
    }

    strong::after {
      content: ':';
      margin-right: 5px;
    }
  `,
);

type Props = {
  onSubmit: HandleSubmitType;
  externalInputSubmit?: boolean;
};

const StepReview = ({ onSubmit, externalInputSubmit = false }: Props) => {
  const [formError, setFormError] = useState<ErrorMessageType>(null);
  const { formData } = useContext(FormDataContext);

  const throttleEnabled = !!formData.azureThrottleEnabled?.value;
  const storeFullMessage = !!formData.azureStoreFullMessage?.value;
  const enableProxy = !!formData.enableProxy?.value;

  const [saveInput, setSaveInput] = useFetch(
    null,
    () => onSubmit(),
    'POST',
    toAzureLogsInputCreateRequest(formData),
    urlWithEventHubParams,
  );

  useEffect(() => {
    setSaveInput(null);

    if (saveInput.error) {
      setFormError({
        full_message: saveInput.error,
        nice_message: <span>We were unable to save your Input, please try again in a few moments.</span>,
      });
    }
  }, [saveInput.error, setSaveInput]);

  const handleSubmit = () => {
    if (externalInputSubmit) {
      onSubmit(formData);

      return;
    }

    setSaveInput(ApiRoutes.INTEGRATIONS.Azure.SAVE_INPUT);
  };

  return (
    <FormWrap
      onSubmit={handleSubmit}
      buttonContent="Save and Start Input"
      loading={saveInput.loading}
      error={formError}
      description="Check out everything below to make sure it's correct, then click the button below to complete your Azure setup!">
      <Container>
        <Subheader>Input Configuration</Subheader>
        <ReviewItems>
          <li>
            <strong>Name</strong>
            <span>{formData.azureName.value}</span>
          </li>
          <li>
            <strong>Azure Event Hub Name</strong>
            <span>{formData.eventHubName.value}</span>
          </li>
          <li>
            <strong>Consumer Group</strong>
            <span>{formData.consumerGroup.value}</span>
          </li>
          <li>
            <strong>Content Format Type</strong>
            <span>{formData.contentTypes?.value}</span>
          </li>
          <li>
            <strong>Maximum Batch Size</strong>
            <span>{formData.azureBatchSize.value}</span>
          </li>
          <li>
            <strong>Maximum Wait Time</strong>
            <span>{formData.azureWaitTime.value}</span>
          </li>
          <li>
            <strong>Enable Throttling</strong>
            <span>
              <StatusIcon active={throttleEnabled} />
            </span>
          </li>
          <li>
            <strong>Store Full Message</strong>
            <span>
              <StatusIcon active={storeFullMessage} />
            </span>
          </li>
          <li>
            <strong>Enable Proxy</strong>
            <span>
              <StatusIcon active={enableProxy} />
            </span>
          </li>
          <li>
            <strong>Proxy URI</strong>
            <span>{formData.proxyOptions.value}</span>
          </li>
        </ReviewItems>
      </Container>
    </FormWrap>
  );
};

export default StepReview;
